import { Button, Card, DatePicker, Form, Typography } from "antd";
import ListTable from "common/ListTable";
import moment from "moment";
import { useEffect, useState } from "react";
import "./index.css";
import useFetchData from "./useFetchData";
import CustomDatePicker from "common/CustomDatePicker";

const getDefaultDate = () => moment();

export default function SamJustification() {
  const { data, setData, onFinish } = useFetchData();
  const [form] = Form.useForm();
  const dateWatcher = Form.useWatch("date", form);
  // Round 3 (final fix, I-3): gates the table/hint now that the mount-time
  // auto-fetch is removed -- see the mount effect below.
  const [hasSearched, setHasSearched] = useState(false);

  // Round 3 (final fix, I-3): whole-branch review found this page misclassified by the
  // warm-rule verdict -- T1 measured cold 214.89s / warm 3.00s with the SAME params, and
  // SamJustificationService.php:53-93 shows the gap is MySQL buffer-pool warmth (the
  // SamJustification::create() write is commented out at L88), not a cache. So the first
  // person to open this page each morning was paying ~3.5 minutes on mount and blocking the
  // single-worker API for everyone -- exactly the cost round 3 exists to catch. The mount
  // effect now only seeds the form; the form.submit() call that followed it is removed.
  useEffect(() => {
    form.setFieldsValue({ date: getDefaultDate() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Round 3 (final fix, I-3): set hasSearched at the top of the fetch path the Submit
  // button calls (Form's onFinish), same as the Preview/Search handlers on the other
  // press-preview pages.
  const handleSubmit = (values) => {
    setHasSearched(true);
    onFinish(values);
  };

  return (
    <Card title={"Sam Justification"}>
      <Form
        layout="horizontal"
        onFinish={handleSubmit}
        form={form}
        initialValues={{ date: getDefaultDate() }}
      >
        <div
          style={{
            display: "flex",
          }}
        >
          <Form.Item label="Date" name={"date"}>
            <CustomDatePicker  size="medium"/>
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            style={{
              marginLeft: "5px",
            }}
          >
            Submit
          </Button>
          <Button
            type="default"
            htmlType="button"
            danger
            style={{
              marginLeft: "5px",
            }}
            onClick={() => {
              form.resetFields();
              setData({
                dateSource: [],
                isLoading: false,
              });
              // Round 3 (final fix, I-3): return to the pre-search state so the
              // hint reappears instead of leaving a stale, now-empty table on screen.
              setHasSearched(false);
            }}
          >
            Reset
          </Button>
        </div>
      </Form>

      {!hasSearched && (
        <Typography.Text type="secondary">
          Press Submit to load this report. It can take a few minutes on the first load of the
          day.
        </Typography.Text>
      )}

      {hasSearched && (
        <ListTable
          size="small"
          columns={columns}
          dataSource={data.dateSource}
          loading={data.isLoading}
          className="SamJustification"
          pagination={false}
          bordered
          // Fix round 1 (ux-sweep batch 2, finding 4): spec A3 hint — the
          // mount fetch's default window (today) is confirmed to return zero
          // rows on this data snapshot; say so instead of a bare "No data".
          locale={{
            emptyText: `No records for ${moment(dateWatcher || getDefaultDate()).format(
              "DD-MMM-YYYY",
            )}. Try a different date.`,
          }}
        />
      )}
    </Card>
  );
}

const columns = [
  {
    title: "Line No",
    dataIndex: "line",
    className: "sam-justification",
    key: "line",
    render: (text, record) => {
      return {
        children: text,
        props: {
          rowSpan: record.rowSpan,
        },
      };
    },
  },
  {
    title: "Style",
    dataIndex: "style",
    className: "sam-justification",
    render: (text, record) => {
      return {
        children: text,
        props: {
          rowSpan: record.rowSpan,
        },
      };
    },
  },
  {
    title: "Operation",
    dataIndex: "operation_name",
    className: "sam-justification",
  },
  {
    title: "Condition",
    dataIndex: "condition",
    className: "sam-justification",
  },
  {
    title: "Operator Name",
    dataIndex: "assigned_operator",
    className: "sam-justification",
  },
  {
    title: "Operator Id Card",
    dataIndex: "assigned_operator_id_card",
    className: "sam-justification",
  },
  { title: "Sam", dataIndex: "sam", className: "sam-justification" },
  {
    title: "Production per Hour",
    className: "production_per_hour",
    children: [
      {
        title: "OB",
        className: "production_per_hour_ob",
        dataIndex: "production_per_hour_ob",
      },
      {
        title: "Actual",
        dataIndex: "production_per_hour_actual",
        className: "production_per_hour_actual",
      },
    ],
  },
  {
    title: "SAM",
    className: "sam_data",
    children: [
      {
        title: "OB",
        className: "sam_data_ob",
        dataIndex: "sam_data_ob",
      },
      {
        title: "Actual",
        dataIndex: "sam_data_actual",
        className: "sam_data_actual",
      },
      {
        title: "Difference",
        dataIndex: "sam_data_difference",
        className: "sam_data_difference",
      },
    ],
  },
];
